// externals
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// types
import type { PackagesSectionViewModel } from '../types/InstalledOptionalEquipmentProps';

import { CDL_V3_EVENT } from '@makemydeal/dr-shared-types';

// store
import { vehicleDetailsSelectors } from '../store';

/**
 * View model for the `PackagesSection` component.
 *
 * Reads the installed optional equipment from the store and owns the accordion
 * expand/collapse state as well as the standard-features slideout state, exposing
 * derived flags and handlers so the component stays presentational.
 */
export const usePackagesEquipment = (): PackagesSectionViewModel => {
    const dispatch = useDispatch();
    const installedOptionalEquipment = useSelector(vehicleDetailsSelectors.getInstalledOptionalEquipment);
    const [expandedIndices, setExpandedIndices] = useState<Set<number>>(new Set());
    const [isStandardFeaturesOpen, setIsStandardFeaturesOpen] = useState(false);

    const optionalPackages = installedOptionalEquipment?.optionalPackages ?? [];
    const standaloneOptions = installedOptionalEquipment?.standaloneOptions ?? [];
    const trimHighlights = installedOptionalEquipment?.trimHighlights ?? [];

    const hasPackages = optionalPackages.length > 0;
    const hasStandalone = standaloneOptions.length > 0;
    const hasHighlights = trimHighlights.length > 0;
    const showBottomRow = hasStandalone || hasHighlights;
    const allExpanded = hasPackages && expandedIndices.size === optionalPackages.length;

    const togglePackage = (index: number) => {
        setExpandedIndices((prev) => {
            const next = new Set(prev);
            if (next.has(index)) {
                next.delete(index);
            } else {
                next.add(index);
            }
            return next;
        });
    };

    const toggleExpandCollapseAll = () => {
        setExpandedIndices(allExpanded ? new Set() : new Set(optionalPackages.map((_, index) => index)));
    };

    return {
        hasEquipment: !!installedOptionalEquipment,
        trim: installedOptionalEquipment?.trim,
        optionalPackages,
        standaloneOptions,
        trimHighlights,
        hasPackages,
        hasStandalone,
        hasHighlights,
        showBottomRow,
        allExpanded,
        isStandardFeaturesOpen,
        isPackageExpanded: (index: number) => expandedIndices.has(index),
        togglePackage,
        toggleExpandCollapseAll,
        openStandardFeatures: () => {
            dispatch({
                type: CDL_V3_EVENT,
                payload: {
                    action: 'Click',
                    element: 'View Standard Vehicle Features Link',
                    location: ['Vehicle Details', 'Installed Optional Equipment'],
                    intention: 'View overview of standard vehicle features',
                    tagIndex: 'dc-sci-072'
                }
            });
            setIsStandardFeaturesOpen(true);
        },
        closeStandardFeatures: () => setIsStandardFeaturesOpen(false)
    };
};
